import { LitElement, html, css, nothing } from 'lit-element';
import { when } from 'lit/directives/when.js';

import {
  CreateCompanyPaymentMethod,
  GetCompanyPaymentMethods,
  VerifyCompanyBankAccount,
  SetPaymentMethodUsedForCompanyKatapultProSubscription,
  RemoveCompanyPaymentMethod,
  GetCompanyBillingInfo,
  SetCompanyBillingInfo,
  SanitizeBillingInfo,
  CreateCustomerPortalSession,
  CheckCompanyManualBilling
} from '../../modules/KatapultBilling';
import { capitalCase } from 'change-case';
import { DateTime, Info as LuxonInfo } from 'luxon';

import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import SlBadge from '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import SlInput from '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';
import '@shoelace-style/shoelace/dist/components/radio-button/radio-button.js';
import { serialize, getFormControls } from '@shoelace-style/shoelace/dist/utilities/form.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';
import { KatapultButton } from '../katapult-elements/katapult-button.js';

/** @import { KatapultBilling } from '../../../../functions/source/server_modules/katapult_billing/KatapultBillingTypes.d.ts' */
/** @import { TemplateResult } from 'lit-element' */
/** @import firebase from 'firebase/compat/app' */
/** @import { Ref } from 'lit/directives/ref.js' */

const FIVE_MINUTES_IN_MILLISECONDS = 5 * 60 * 1000;

const FORM_STYLES = css`
  form {
    font-size: var(--sl-font-size-small);
  }
  form .row {
    display: flex;
    gap: var(--sl-spacing-small);
  }
  form .column {
    display: flex;
    flex-direction: column;
    gap: var(--sl-spacing-small);
  }
  form .label {
    font-weight: var(--sl-font-weight-semibold);
    font-size: var(--sl-font-size-medium);
  }
  form .card-expiration sl-input::part(form-control-label) {
    white-space: nowrap;
  }
  form .card-expiration sl-input::part(form-control-input) {
    text-align: center;
  }
  form .inputWrapper {
    min-width: 0;
    align-content: end;
    flex-basis: 0;
    flex-grow: 1;
  }
`;

class CompanyBillingOptions extends LitElement {
  static properties = {
    _billingInfo: { type: Object, state: true },
    companyId: { type: String, shouldUpdate: false },
    _companyUsingManualBilling: { type: Boolean, state: true },
    _paymentMethods: { type: Array, state: true },
    _unverifiedPaymentMethods: { type: Array, state: true },
    _subscriptionPaymentMethod: { type: Object, state: true },
    _opened: { type: Boolean, state: true, shouldUpdate: false },

    // Payment methods loading state properties.
    _loadingBillingInfo: { type: Boolean, state: true },
    _billingInfoLastUpdatedAt: { type: Number, state: true, shouldUpdate: false },
    _loadBillingInfoError: { type: Object, state: true },
    _hasLoadedBillingInfo: { type: Boolean, state: true },

    // Payment methods loading state properties.
    _loadingPaymentMethods: { type: Boolean, state: true },
    _paymentMethodsLastUpdatedAt: { type: Number, state: true, shouldUpdate: false },
    _loadPaymentMethodsError: { type: Object, state: true },
    _hasLoadedPaymentMethods: { type: Boolean, state: true }
  };

  static styles = [
    FORM_STYLES,
    css`
      .section {
        display: flex;
        flex-direction: column;
        padding: var(--sl-spacing-small);
        padding-bottom: var(--sl-spacing-medium);
      }

      /* .section:not(:last-child) {
        border-bottom: 1px solid var(--sl-color-neutral-200);
      } */

      .section .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      table {
        border: 1px solid var(--sl-color-neutral-200);
        border-radius: var(--sl-border-radius-large);
        border-spacing: unset;
        font-size: var(--sl-font-size-small);
      }

      td {
        padding: var(--sl-spacing-x-small);
      }

      td:first-child {
        font-weight: var(--sl-font-weight-semibold);
        color: var(--sl-color-neutral-800);
      }

      tr:not(:last-child) td {
        border-bottom: 1px solid var(--sl-color-neutral-200);
      }

      #paymentMethods {
        display: flex;
        flex-direction: column;
        gap: var(--sl-spacing-small);
      }

      .message {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--sl-spacing-2x-small);
        font-size: var(--sl-font-size-small);
      }

      sl-spinner {
        font-size: var(--sl-font-size-x-large);
        --track-width: 3px;
      }
    `
  ];

  constructor() {
    super();

    /** @type {?string} */
    this.companyId = null;

    /** @type {?boolean} */
    this._companyUsingManualBilling = null;

    /** @type {boolean} */
    this._opened = false;

    /** @type {KatapultBilling.PaymentMethodSummary[]} */
    this._paymentMethods = [];

    /** @type {KatapultBilling.PaymentMethodSummary[]} */
    this._unverifiedPaymentMethods = [];

    /** @type {?KatapultBilling.PaymentMethodSummary} */
    this._subscriptionPaymentMethod = null;

    // Billing Info Loading State Properties.
    /** @type {boolean} */
    this._loadingBillingInfo = false;
    /** @type {?number} */
    this._billingInfoLastUpdatedAt = null;
    /** @type {?Error} */
    this._loadBillingInfoError = null;
    /** @type {boolean} */
    this._hasLoadedBillingInfo = false;

    // Payment methods loading state properties.
    /** @type {boolean} */
    this._loadingPaymentMethods = false;
    /** @type {?number} */
    this._paymentMethodsLastUpdatedAt = null;
    /** @type {?Error} */
    this._loadPaymentMethodsError = null;
    /** @type {boolean} */
    this._hasLoadedPaymentMethods = false;

    /** @type {?KatapultBilling.CompanyBillingInfo} */
    this._billingInfo = null;

    /** @type {Ref<KatapultDialog>} */
    this._addCreditCardDialogRef = createRef();

    /** @type {Ref<HTMLFormElement>} */
    this._addCreditCardFormRef = createRef();

    /** @type {Ref<KatapultDialog>} */
    this._addBankAccountDialogRef = createRef();

    /** @type {Ref<HTMLFormElement>} */
    this._addBankAccountFormRef = createRef();

    /** @type {Ref<KatapultDialog>} */
    this._billingInfoDialogRef = createRef();

    /** @type {Ref<HTMLFormElement>} */
    this._billingInfoFormRef = createRef();
  }

  /**
   * Renders the template for the company info table.
   * @returns {TemplateResult}
   */
  renderBillingInfoTable() {
    const { _billingInfo } = this;
    const { address, phone, email } = _billingInfo ?? {};
    const { line1, line2, city, state, postal_code: postalCode, country } = address ?? {};

    const cityState = [city, state].filter((x) => !!x).join(', ');
    const line3 = [cityState, postalCode].filter((x) => !!x).join(' ');

    const addressLines = [line1, line2, line3, country];
    const formattedAddress = addressLines.filter((x) => !!x).join('\n');

    const formattedPhone = this.#formatPhoneNumber(phone);

    return html`
      <table style="white-space: pre-wrap;">
        <tr>
          <td>Address</td>
          <td>${formattedAddress}</td>
        </tr>
        <tr>
          <td>Email</td>
          <td>${email}</td>
        </tr>
        <tr>
          <td>Phone</td>
          <td>${formattedPhone}</td>
        </tr>
        <tr></tr>
      </table>
    `;
  }

  /**
   * Formats a US phone number to (XXX) XXX-XXXX format, if possible.
   * @param {String | Number} phoneNumber
   * @returns {String}
   */
  #formatPhoneNumber(phoneNumber) {
    if (phoneNumber == null) return '';
    const stringPhoneNumber = String(phoneNumber);
    const cleaned = this.#phoneNumberToDigitString(String(stringPhoneNumber));
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    return stringPhoneNumber;
  }

  /**
   * Formats a phone number to a string of only digits.
   * @param {String} phoneNumber
   * @returns {String}
   */
  #phoneNumberToDigitString(phoneNumber) {
    if (typeof phoneNumber !== 'string') return '';
    return phoneNumber.replace(/\D/g, '');
  }

  /**
   * Renders the template for the add credit card dialog.
   * @returns {TemplateResult}
   */
  #renderAddCreditCardDialog() {
    return html`
      <katapult-dialog
        ${ref(this._addCreditCardDialogRef)}
        title="Add a Credit Card"
        .icon=${PaymentMethod.GetIcon('card')}
        @closed=${() => this._addCreditCardFormRef.value?.reset()}
      >
        <form ${ref(this._addCreditCardFormRef)} @submit=${(e) => e.preventDefault()}>
          <div class="row" style="gap: var(--sl-spacing-large);">
            <div class="column" style="width: 320px;">
              <div class="label">Card Information</div>
              <sl-input size="small" name="card_holder_name" label="Card Holder's Name" required></sl-input>
              <sl-input
                size="small"
                name="card_number"
                label="Card Number"
                @sl-input=${this.#creditCardInputHandler}
                pattern="([0-9]{4} ?){3}[0-9]{4}( [0-9]{3})?"
                help-text="Accepts 16-digit or 19-digit card numbers"
                required
              ></sl-input>
              <div class="row card-expiration">
                <div class="inputWrapper">
                  <sl-input
                    size="small"
                    type="number"
                    no-spin-buttons
                    name="expiration_month"
                    label="Expiration Date"
                    placeholder="MM"
                    min="1"
                    max="12"
                    required
                  ></sl-input>
                </div>
                <div class="inputWrapper">
                  <sl-input
                    size="small"
                    type="number"
                    no-spin-buttons
                    name="expiration_year"
                    label=""
                    placeholder="YY"
                    min=${new Date().getFullYear().toString().slice(-2)}
                    max="99"
                    required
                  ></sl-input>
                </div>
                <div class="inputWrapper">
                  <sl-input
                    size="small"
                    name="security_code"
                    label="Security Code"
                    @sl-input=${this.#creditCardExpirationInputHandler}
                    placeholder="CVC"
                    pattern="[0-9]{3,4}"
                    maxLength="4"
                    required
                  ></sl-input>
                </div>
              </div>
            </div>
            <div class="column" style="width: 320px;">
              <div class="row" style="justify-content: space-between;">
                <div class="label">Billing Address</div>
                <katapult-button size="18" icon="arrow_downward" .callback=${this.#addCreditCardFormUseCompanyBillingAddress.bind(this)}
                  >Use Company Address</katapult-button
                >
              </div>
              <div>Use the billing address associated with this credit card.</div>
              <sl-input size="small" name="billing_name" label="Name" required></sl-input>
              <sl-input size="small" name="billing_address_line_1" label="Address Line 1" required></sl-input>
              <sl-input size="small" name="billing_address_line_2" label="Address Line 2"></sl-input>
              <sl-input size="small" name="billing_city" label="City" required></sl-input>
              <div class="row">
                <div class="inputWrapper"><sl-input size="small" name="billing_state" label="State/Region" required></sl-input></div>
                <div class="inputWrapper"><sl-input size="small" name="billing_zip_code" label="Zip Code" required></sl-input></div>
              </div>
              <sl-input
                size="small"
                name="billing_country"
                label="Country"
                pattern="[a-zA-z]{2}"
                placeholder="US"
                required
                help-text="Accepts 2 letter country code"
              ></sl-input>
            </div>
          </div>
        </form>
        <katapult-button slot="buttons" dialog-dismiss>Cancel</katapult-button>
        <katapult-button
          slot="buttons"
          color="var(--secondary-color)"
          .icon=${PaymentMethod.GetIcon('card')}
          .callback=${this.#confirmAddCreditCard.bind(this)}
          >Add Credit Card</katapult-button
        >
      </katapult-dialog>
    `;
  }

  #renderAddBankAccountDialog() {
    return html`
      <katapult-dialog
        ${ref(this._addBankAccountDialogRef)}
        title="Add a Bank Account"
        .icon=${PaymentMethod.GetIcon('us_bank_account')}
        @closed=${() => this._addBankAccountFormRef.value?.reset()}
      >
        <form ${ref(this._addBankAccountFormRef)} @submit=${(e) => e.preventDefault()}>
          <div class="row" style="gap: var(--sl-spacing-large);">
            <div class="column" style="width: 320px;">
              <div class="label">Bank Information</div>
              <div>
                Your bank's routing number is the number on the bottom-left of your checks. The account number is the next number to the
                right.
              </div>
              <sl-input size="small" type="number" no-spin-buttons name="routing_number" label="Routing Number" required></sl-input>
              <sl-input size="small" type="number" no-spin-buttons name="account_number" label="Account Number" required></sl-input>
              <sl-input size="small" name="account_holder_name" label="Account Holder Name" required></sl-input>
              <sl-radio-group size="small" label="Account Holder Type" name="account_holder_type" required>
                <sl-radio-button value="individual">Individual</sl-radio-button>
                <sl-radio-button value="company">Company</sl-radio-button>
              </sl-radio-group>
            </div>
            <div class="column" style="width: 320px;">
              <div class="label">Authorization</div>
              <sl-checkbox size="small" required
                >I authorize Katapult Engineering to electronically debit my account and, if necessary, electronically credit my account to
                correct erroneous debits.</sl-checkbox
              >
              <div>
                At any time you may request Katapult Engineering to prepare a detailed history of transactions, both debits and credits,
                that we have made to your account.
              </div>
              <div>
                After you add this account, you will need to verify it. In the next 1-2 days, look for two small deposits (less than $1.00)
                to appear on your bank account. When they appear return here to the admin page, and find this account in the payment methods
                list. Select "Verify Bank Account" under the account's three dot menu, and enter the two deposited amounts when prompted.
                You may then choose to use this account as the default payment method for your Katapult Pro subscription.
              </div>
            </div>
          </div>
        </form>
        <katapult-button slot="buttons" dialog-dismiss>Cancel</katapult-button>
        <katapult-button
          slot="buttons"
          color="var(--secondary-color)"
          .icon=${PaymentMethod.GetIcon('us_bank_account')}
          .callback=${this.#confirmAddBankAccount.bind(this)}
          >Add Bank Account</katapult-button
        >
      </katapult-dialog>
    `;
  }

  /**
   * Renders the template for the add credit card dialog.
   * @returns {TemplateResult}
   */
  #renderEditBillingInfoDialog() {
    return html`
      <katapult-dialog
        ${ref(this._billingInfoDialogRef)}
        title="Billing Information"
        icon="business"
        @closed=${() => this._billingInfoFormRef.value?.reset()}
      >
        <form ${ref(this._billingInfoFormRef)} @submit=${(e) => e.preventDefault()}>
          <div class="column" style="width: 320px;">
            <sl-input size="small" name="billing_address_line_1" label="Address Line 1" required> </sl-input>
            <sl-input size="small" name="billing_address_line_2" label="Address Line 2"> </sl-input>
            <sl-input size="small" name="billing_address_city" label="City" required> </sl-input>
            <div class="row">
              <div class="inputWrapper">
                <sl-input size="small" name="billing_address_state" label="State/Region" required> </sl-input>
              </div>
              <div class="inputWrapper">
                <sl-input size="small" name="billing_address_zip_code" label="Zip Code" required> </sl-input>
              </div>
            </div>
            <sl-input
              size="small"
              name="billing_address_country"
              label="Country"
              pattern="[a-zA-z]{2}"
              placeholder="US"
              required
              help-text="Accepts 2 letter country code"
            ></sl-input>
            <sl-input size="small" name="billing_contact_email" label="Email" type="email" required> </sl-input>
            <sl-input
              size="small"
              name="billing_phone"
              label="Phone"
              type="tel"
              pattern="\\(\\d{3}\\)\\s\\d{3}-\\d{4}"
              @sl-change=${(e) => (e.currentTarget.value = this.#formatPhoneNumber(e.currentTarget.value))}
              required
              help-text="(XXX) XXX-XXXX"
            >
            </sl-input>
          </div>
        </form>
        <katapult-button slot="buttons" dialog-dismiss>Cancel</katapult-button>
        <katapult-button slot="buttons" color="var(--secondary-color)" .callback=${this.#confirmEditBillingInfo.bind(this)}
          >Save</katapult-button
        >
      </katapult-dialog>
    `;
  }

  render() {
    const performingInitialBillingInfoLoad = this._loadingBillingInfo && !this._hasLoadedBillingInfo;
    const performingInitialPaymentMethodsLoad = this._loadingPaymentMethods && !this._hasLoadedPaymentMethods;
    return html`
      ${this.#renderAddCreditCardDialog()} ${this.#renderAddBankAccountDialog()} ${this.#renderEditBillingInfoDialog()}
      <paper-header-content @opened-changed=${(e) => (this._opened = e.detail.value)}>
        <div
          slot="header"
          style="display: flex; gap: var(--sl-spacing-small); align-items: center; flex-grow: 1; padding: var(--sl-spacing-small);"
        >
          <katapult-icon icon="payments"></katapult-icon>
          <div slot="header">Billing</div>
        </div>
        <div slot="content" style="display: content;">
          <div class="section">
            <div class="header">
              <div>Billing Information</div>
              <div style="flex-grow: 1;"></div>
              ${when(
                !performingInitialBillingInfoLoad && !this._loadBillingInfoError,
                () =>
                  html` <sl-tooltip content="Edit Billing Information">
                    <katapult-button iconOnly noBorder icon="edit" .callback=${this.#promptEditBillingInfo.bind(this)}></katapult-button>
                  </sl-tooltip>`
              )}
            </div>

            ${when(
              performingInitialBillingInfoLoad,
              () => html`<sl-spinner style="align-self: center;"></sl-spinner>`,
              () =>
                when(
                  this._loadBillingInfoError,
                  () =>
                    html` <div class="message" style="color: var(--sl-color-danger-500);">
                      <katapult-icon icon="error"></katapult-icon>
                      <div>Failed to load billing info</div>
                    </div>`,
                  () => this.renderBillingInfoTable()
                )
            )}
          </div>
          <div class="section">
            <div class="header">
              <div>Payment Methods</div>
              <div style="flex-grow: 1;"></div>
              ${when(
                !performingInitialPaymentMethodsLoad && this._paymentMethods.length > 0,
                () =>
                  html` <sl-dropdown style="position: relative;" hoist>
                    <div slot="trigger">
                      <sl-tooltip content="Add New Payment Method">
                        <katapult-button iconOnly noBorder icon="add_circle" .iconConfig=${{ outline: true }}></katapult-button>
                      </sl-tooltip>
                    </div>
                    <sl-menu @sl-select=${this.#addPaymentMethodMenuSelectHandler.bind(this)}>
                      <sl-menu-item value="card">
                        <katapult-icon slot="prefix" .icon=${PaymentMethod.GetIcon('card')} size="20"></katapult-icon>
                        <div>Add a Credit Card</div>
                      </sl-menu-item>
                      <sl-menu-item value="us_bank_account">
                        <katapult-icon slot="prefix" .icon=${PaymentMethod.GetIcon('us_bank_account')} size="20"></katapult-icon>
                        <div>Add a Bank Account</div>
                      </sl-menu-item>
                    </sl-menu>
                  </sl-dropdown>`
              )}
            </div>
            <div id="paymentMethods">
              ${when(
                performingInitialPaymentMethodsLoad,
                () => html`<sl-spinner style="align-self: center;"></sl-spinner>`,
                () =>
                  when(
                    this._loadPaymentMethodsError,
                    () =>
                      html` <div class="message" style="color: var(--sl-color-danger-500);">
                        <katapult-icon icon="error"></katapult-icon>
                        <div>Failed to load payment methods</div>
                      </div>`,
                    () =>
                      when(
                        this._companyUsingManualBilling === true,
                        () =>
                          html` <div class="message" style="color: var(--sl-color-neutral-800);">
                            <katapult-icon icon="contract"></katapult-icon>
                            <div>Manual invoice requested</div>
                          </div>`,
                        () =>
                          when(
                            this._paymentMethods.length === 0,
                            () =>
                              html` <div class="message">
                                <div style="font-size: var(--sl-font-size-large);">😞</div>
                                <div>You have no payment methods</div>
                                <katapult-button
                                  style="margin-top: var(--sl-spacing-small);"
                                  icon="add_circle"
                                  size="18"
                                  color="var(--secondary-color)"
                                  >Add a Payment Method</katapult-button
                                >
                              </div>`,
                            () =>
                              this._paymentMethods.map((paymentMethod) => {
                                const usedByKatapultProSubscription = paymentMethod.id === this._subscriptionPaymentMethod?.id;
                                const unverified =
                                  this._unverifiedPaymentMethods.find((unverified) => unverified.id == paymentMethod.id) != null;
                                return html`<payment-method
                                  .companyId=${this.companyId}
                                  .paymentMethod=${paymentMethod}
                                  .usedByKatapultProSubscription=${usedByKatapultProSubscription}
                                  .unverified=${unverified}
                                  @verified=${this.#updatePaymentMethods.bind(this)}
                                  @used-for-katapult-pro-subscription=${this.#updatePaymentMethods.bind(this)}
                                  @removed=${this.#updatePaymentMethods.bind(this)}
                                ></payment-method>`;
                              })
                          )
                      )
                  )
              )}
            </div>
          </div>
          <div class="section" style="flex-direction: row; align-items: center; justify-content: center">
            <span style="font-size: 12px;">See transaction history and subscription details</span>
            <katapult-button
              icon="open_in_new"
              .callback=${this.#openCustomerPortal.bind(this)}
              iconOnly
              noBorder
              size="20"
            ></katapult-button>
          </div>
        </div>
      </paper-header-content>
    `;
  }

  shouldUpdate(changedProperties) {
    if (changedProperties.has('companyId')) {
      const editBillingInfoDialog = this._billingInfoDialogRef.value;
      editBillingInfoDialog?.close();
    }

    if (['companyId', '_opened'].some((x) => changedProperties.has(x))) {
      const delayUpdate = changedProperties.has('_opened') && this._opened != true;
      setTimeout(
        () => {
          this.#updateBillingInfo();
          this.#updatePaymentMethods();
        },
        delayUpdate ? 300 : 0
      );
    }

    if (['companyId', '_opened', '_billingInfoLastUpdatedAt'].some((x) => changedProperties.has(x))) {
      if (this._updateBillingInfoTimeout != null) {
        clearTimeout(this._updateBillingInfoTimeout);
        this._updateBillingInfoTimeout = null;
      }

      if (this._opened && this.companyId != null) {
        this._updateBillingInfoTimeout = setInterval(() => this.#updateBillingInfo(), FIVE_MINUTES_IN_MILLISECONDS);
      }
    }

    if (['companyId', '_opened', '_paymentMethodsLastUpdatedAt'].some((x) => changedProperties.has(x))) {
      if (this._updatePaymentMethodsTimeout != null) {
        clearTimeout(this._updatePaymentMethodsTimeout);
        this._updatePaymentMethodsTimeout = null;
      }

      if (this._opened && this.companyId != null) {
        this._updatePaymentMethodsTimeout = setInterval(() => this.#updatePaymentMethods(), FIVE_MINUTES_IN_MILLISECONDS);
      }
    }

    const updatedCalledManually = changedProperties.size === 0;
    const meaningfulChange = changedProperties.keys().some((key) => this.constructor.properties[key]?.shouldUpdate !== false);
    const shouldUpdate = updatedCalledManually || meaningfulChange;
    return shouldUpdate;
  }

  #addPaymentMethodMenuSelectHandler(e) {
    const { item } = e.detail;
    const { value } = item;
    switch (value) {
      case 'card':
        this.#promptAddCreditCard();
        break;
      case 'us_bank_account':
        this.#promptAddUsBankAccountPaymentMethod();
        break;
    }
  }

  #addCreditCardFormUseCompanyBillingAddress(e) {
    const form = this._addCreditCardFormRef.value;
    if (!form) throw new Error('Form not found');
    const formControls = /** @type {Array<SlInput>} */ (getFormControls(form));

    const { _billingInfo } = this;
    const { address } = _billingInfo ?? {};
    const companyBillingData = {
      billing_address_line_1: address?.line1 ?? '',
      billing_address_line_2: address?.line2 ?? '',
      billing_city: address?.city ?? '',
      billing_state: address?.state ?? '',
      billing_zip_code: address?.postal_code ?? '',
      billing_country: address?.country ?? ''
    };

    formControls.forEach((input) => {
      const { name } = input;
      if (name == null || !companyBillingData.hasOwnProperty(name)) return;
      input.value = companyBillingData[name];
    });
  }

  #creditCardInputHandler(e) {
    /** @type {SlInput} */
    const input = e.currentTarget;
    const { value } = input;
    const cleaned = value.replace(/\D/g, '').slice(0, 19);
    const formatted = cleaned.match(/\d{1,4}/g)?.join(' ') ?? value;
    input.value = formatted;
  }

  #creditCardExpirationInputHandler(e) {
    /** @type {SlInput} */
    const input = e.currentTarget;
    const { value } = input;
    const cleaned = value.replace(/\D/g, '').slice(0, input.maxlength || undefined);
    input.value = cleaned;
  }

  #promptAddCreditCard() {
    const dialog = this._addCreditCardDialogRef.value;
    if (!dialog) throw new Error('Dialog not found');
    dialog.open();
  }

  async #confirmAddCreditCard(e) {
    const button = /** @type {KatapultButton} */ (e?.currentTarget);
    const dialog = this._addCreditCardDialogRef.value;
    await this.#addCreditCard()
      .then(() => {
        button?.showSuccess();
        setTimeout(() => dialog?.close(), 2000);
      })
      .catch((err) => {
        button?.showError();
        if (err.message !== 'Form is invalid') throw err;
      });
  }

  async #addCreditCard() {
    const companyId = this.companyId;
    if (typeof companyId !== 'string') throw new Error('companyId must be a string');

    const form = this._addCreditCardFormRef.value;
    if (!form) throw new Error('Form not found');

    const formIsValid = form.reportValidity();
    if (!formIsValid) throw new Error('Form is invalid');

    const formData = serialize(form);
    const expirationYear = Number(formData.expiration_year);
    const expirationMonth = Number(formData.expiration_month);
    if (isNaN(expirationYear)) throw new Error('Expiration year is not a number');
    if (isNaN(expirationMonth)) throw new Error('Expiration month is not a number');
    if (typeof formData.card_number !== 'string') throw new Error('Card number must be a string');
    if (typeof formData.security_code !== 'string') throw new Error('Security code must be a string');
    if (typeof formData.billing_name !== 'string') throw new Error('Billing name must be a string');
    if (typeof formData.billing_address_line_1 !== 'string') throw new Error('Billing address line 1 must be a string');
    if (typeof formData.billing_address_line_2 !== 'string') throw new Error('Billing address line 2 must be a string');
    if (typeof formData.billing_city !== 'string') throw new Error('Billing city must be a string');
    if (typeof formData.billing_state !== 'string') throw new Error('Billing state must be a string');
    if (typeof formData.billing_zip_code !== 'string') throw new Error('Billing zip code must be a string');
    if (typeof formData.billing_country !== 'string') throw new Error('Billing country must be a string');

    /**@type {KatapultBilling.CreateCompanyPaymentMethod.PaymentMethodData} */
    const paymentMethodData = {
      type: 'card',
      card: {
        number: formData.card_number.replace(/\D/g, ''),
        exp_month: expirationMonth,
        exp_year: expirationYear,
        cvc: formData.security_code
      },
      billing_details: {
        name: formData.billing_name,
        address: {
          line1: formData.billing_address_line_1,
          line2: formData.billing_address_line_2,
          city: formData.billing_city,
          state: formData.billing_state,
          postal_code: formData.billing_zip_code,
          country: formData.billing_country
        }
      }
    };
    await CreateCompanyPaymentMethod(companyId, paymentMethodData);
    this.#updatePaymentMethods();
  }

  #promptAddUsBankAccountPaymentMethod() {
    const dialog = this._addBankAccountDialogRef.value;
    if (!dialog) throw new Error('Dialog not found');
    dialog.open();
  }

  async #confirmAddBankAccount(e) {
    const button = /** @type {KatapultButton} */ (e?.currentTarget);
    const dialog = this._addBankAccountDialogRef.value;
    await this.#addBankAccount()
      .then(() => {
        button?.showSuccess();
        setTimeout(() => dialog?.close(), 2000);
      })
      .catch((err) => {
        button?.showError();
        if (err.message !== 'Form is invalid') throw err;
      });
  }

  async #addBankAccount() {
    const companyId = this.companyId;
    if (typeof companyId !== 'string') throw new Error('companyId must be a string');

    const form = this._addBankAccountFormRef.value;
    if (!form) throw new Error('Form not found');

    const formIsValid = form.reportValidity();
    if (!formIsValid) throw new Error('Form is invalid');

    const formData = serialize(form);
    if (typeof formData.account_holder_name !== 'string') throw new Error('Account holder name must be a string');
    if (typeof formData.routing_number !== 'string') throw new Error('Routing number must be a string');
    if (typeof formData.account_number !== 'string') throw new Error('Account number must be a string');
    if (typeof formData.account_holder_type !== 'string') throw new Error('Account holder type must be a string');

    /** @type {KatapultBilling.CreateCompanyPaymentMethod.PaymentMethodData.UsBankAccount.AccountHolderType} */
    const accountHolderType = formData.account_holder_type;

    /**@type {KatapultBilling.CreateCompanyPaymentMethod.PaymentMethodData} */
    const paymentMethodData = {
      type: 'us_bank_account',
      us_bank_account: {
        routing_number: formData.routing_number,
        account_number: formData.account_number,
        account_holder_type: accountHolderType
      },
      billing_details: {
        name: formData.account_holder_name
      }
    };
    await CreateCompanyPaymentMethod(companyId, paymentMethodData);
    this.#updatePaymentMethods();
  }

  #populateEditBillingInfoInputValues() {
    const form = this._billingInfoFormRef.value;
    if (!form) throw new Error('Form not found');

    const formControls = /** @type {Array<SlInput>} */ (getFormControls(form));

    const { _billingInfo } = this;
    const { address, phone, email } = _billingInfo ?? {};
    const { line1, line2, city, state, postal_code: postalCode, country } = address ?? {};

    const billingInfoInputValues = {
      billing_address_line_1: line1,
      billing_address_line_2: line2,
      billing_address_city: city,
      billing_address_state: state,
      billing_address_zip_code: postalCode,
      billing_address_country: country,
      billing_phone: this.#formatPhoneNumber(phone ?? ''),
      billing_contact_email: email
    };

    formControls.forEach((input) => {
      const { name } = input;
      if (name == null || !billingInfoInputValues.hasOwnProperty(name)) return;
      input.value = billingInfoInputValues[name] ?? '';
    });
  }

  #promptEditBillingInfo() {
    const dialog = this._billingInfoDialogRef.value;
    if (!dialog) throw new Error('Dialog not found');

    this.#populateEditBillingInfoInputValues();

    dialog.open();
  }

  async #confirmEditBillingInfo(e) {
    const button = /** @type {KatapultButton} */ (e?.currentTarget);
    const dialog = this._billingInfoDialogRef.value;
    await this.#editBillingInfo()
      .then(() => {
        button?.showSuccess();
        setTimeout(() => dialog?.close(), 2000);
        this.#updateBillingInfo();
      })
      .catch((err) => {
        button?.showError();
        if (err.message !== 'Form is invalid') throw err;
      });
  }

  async #editBillingInfo() {
    const companyId = this.companyId;
    if (typeof companyId !== 'string') throw new Error('companyId must be a string');

    const form = this._billingInfoFormRef.value;
    if (!form) throw new Error('Form not found');

    const formIsValid = form.reportValidity();
    if (!formIsValid) throw new Error('Form is invalid');

    const formData = serialize(form);

    const { billingInfo } = SanitizeBillingInfo({
      address: {
        line1: formData.billing_address_line_1,
        line2: formData.billing_address_line_2,
        city: formData.billing_address_city,
        state: formData.billing_address_state,
        postal_code: formData.billing_address_zip_code,
        country: formData.billing_address_country
      },
      phone: this.#phoneNumberToDigitString(formData.billing_phone),
      email: formData.billing_contact_email
    });

    await SetCompanyBillingInfo(companyId, billingInfo);
  }

  /**
   * Loads the payment methods for the company or clears them if there is no company.
   * @returns {Promise<void>}
   */
  async #updatePaymentMethods() {
    const { companyId, _opened } = this;
    const paymentMethods = [];
    const unverifiedPaymentMethods = [];
    let subscriptionPaymentMethod = null;
    try {
      if (_opened && companyId != null) {
        this._loadPaymentMethodsError = null;
        this._loadingPaymentMethods = true;
        this._companyUsingManualBilling = (await CheckCompanyManualBilling(companyId)).manualBilling;
        const companyIsNotUsingManualBilling = this._companyUsingManualBilling === false;
        if (companyIsNotUsingManualBilling) {
          const {
            verifiedPaymentMethods,
            unverifiedPaymentMethods: _unverifiedPaymentMethods,
            katapultProSubscriptionPaymentMethod
          } = await GetCompanyPaymentMethods(companyId);
          subscriptionPaymentMethod = katapultProSubscriptionPaymentMethod;
          unverifiedPaymentMethods.push(..._unverifiedPaymentMethods);
          paymentMethods.push(...verifiedPaymentMethods, ...unverifiedPaymentMethods);
          paymentMethods.sort((a, b) => a.created - b.created);
          this._paymentMethodsLastUpdatedAt = Date.now();
          this._hasLoadedPaymentMethods = true;
        }
      } else {
        this._companyUsingManualBilling = false;
        this._hasLoadedPaymentMethods = false;
      }
    } catch (err) {
      this._loadPaymentMethodsError = err;
    } finally {
      this._paymentMethods = paymentMethods;
      this._unverifiedPaymentMethods = unverifiedPaymentMethods;
      this._subscriptionPaymentMethod = subscriptionPaymentMethod;
      this._loadingPaymentMethods = false;
    }
  }

  /**
   * Loads the billing info for the company or clears it if there is no company.
   * @returns {Promise<void>}
   */
  async #updateBillingInfo() {
    const { companyId, _opened } = this;
    let billingInfo = null;
    try {
      if (_opened && companyId != null) {
        this._loadBillingInfoError = null;
        this._loadingBillingInfo = true;
        billingInfo = await GetCompanyBillingInfo(companyId);
        this._billingInfoLastUpdatedAt = Date.now();
        this._hasLoadedBillingInfo = true;
      } else {
        this._hasLoadedBillingInfo = false;
      }
    } catch (err) {
      this._loadBillingInfoError = err;
    } finally {
      this._billingInfo = billingInfo;
      this._loadingBillingInfo = false;
    }
  }

  async #openCustomerPortal() {
    // Get the customer portal session object
    const { companyId } = this;
    if (!companyId) throw new Error('Missing company ID');

    /* @type KatapultBilling.CustomerPortalSessionSummary */
    const { url: portalSessionObjectUrl } = await CreateCustomerPortalSession(companyId);

    // Open the link
    window.open(portalSessionObjectUrl, '_blank');
  }
}
customElements.define('company-billing-options', CompanyBillingOptions);

class PaymentMethod extends LitElement {
  static properties = {
    active: { type: Boolean },
    paymentMethod: { type: Object },
    unverified: { type: Boolean },
    _renderVerifyBankAccountDialog: { type: Boolean, state: true }
  };

  static styles = [
    FORM_STYLES,
    css`
      :host {
        display: flex;
        justify-content: space-between;
        padding: var(--sl-spacing-2x-small);
        padding-left: var(--sl-spacing-small);
        border: 1px solid var(--sl-color-neutral-200);
        border-radius: var(--sl-border-radius-large);
        font-size: var(--sl-font-size-x-small);
      }

      :host > div {
        display: flex;
        align-items: center;
        gap: var(--sl-spacing-x-small);
      }

      sl-menu-item[disabled]::part(base) {
        color: rgba(0, 0, 0, 0.3);
        opacity: 1;
      }

      sl-menu-item::part(label) {
        display: flex;
        align-items: center;
        gap: var(--sl-spacing-x-small);
      }

      sl-input.cents::part(input) {
        padding-left: 0;
      }
    `
  ];

  /**
   * Returns the icon associated with a payment method type.
   * @param {KatapultBilling.PaymentMethodSummary['type'] | string} [type]
   * @returns {string}
   * */
  static GetIcon(type) {
    switch (type) {
      case 'card':
        return 'credit_card';
      case 'us_bank_account':
        return 'account_balance';
      default:
        return 'help';
    }
  }

  constructor() {
    super();

    /** @type {string | null} */
    this.companyId = null;

    /** @type {KatapultBilling.PaymentMethodSummary | null} */
    this.paymentMethod = null;

    /** @type {boolean} */
    this.usedByKatapultProSubscription = false;

    /** @type {boolean} */
    this.unverified = false;

    /** @type {Ref<KatapultDialog>} */
    this._verifyBankAccountDialogRef = createRef();

    /** @type {Ref<HTMLFormElement>} */
    this._verifyBankAccountFormRef = createRef();

    /** @type {boolean} */
    this._renderVerifyBankAccountDialog = false;
  }

  /**
   * Renders template for the bank account verification dialog.
   * @returns {TemplateResult | nothing}
   */
  #renderVerifyBankAccountDialog() {
    if (!this._renderVerifyBankAccountDialog) return nothing;

    const { paymentMethod } = this;
    if (!paymentMethod) return nothing;

    const label = this.#getLabel();
    const { created } = paymentMethod;
    const createdDate = DateTime.fromSeconds(created).toLocaleString(DateTime.DATE_FULL);
    return html`
      <katapult-dialog
        ${ref(this._verifyBankAccountDialogRef)}
        title="Verify Bank Account"
        .icon=${PaymentMethod.GetIcon('us_bank_account')}
        @closed=${() => (this._renderVerifyBankAccountDialog = false)}
      >
        <form ${ref(this._verifyBankAccountFormRef)} @submit=${(e) => e.preventDefault()}>
          <div class="row" style="gap: var(--sl-spacing-large);">
            <div class="column" style="width: 320px;">
              <div style="text-align: center;">Please enter the two small deposits made to ${label} on or around ${createdDate}.</div>
              <div class="row">
                <div class="inputWrapper">
                  <sl-input
                    class="cents"
                    size="small"
                    type="number"
                    no-spin-buttons
                    name="micro_deposit_1"
                    placeholder="00"
                    min="0"
                    max="99"
                    required
                  >
                    <span slot="prefix">$0.</span>
                  </sl-input>
                </div>
                <div class="inputWrapper">
                  <sl-input
                    class="cents"
                    size="small"
                    type="number"
                    no-spin-buttons
                    name="micro_deposit_2"
                    placeholder="00"
                    min="0"
                    max="99"
                    required
                  >
                    <span slot="prefix">$0.</span>
                  </sl-input>
                </div>
              </div>
            </div>
          </div>
        </form>
        <katapult-button slot="buttons" dialog-dismiss>Cancel</katapult-button>
        <katapult-button
          slot="buttons"
          color="var(--secondary-color)"
          .icon=${PaymentMethod.GetIcon('us_bank_account')}
          .callback=${this.#confirmVerifyBankAccount.bind(this)}
          >Verify Bank Account</katapult-button
        >
      </katapult-dialog>
    `;
  }

  render() {
    const icon = this.#getIcon();
    const label = this.#getLabel();
    const badges = this.#getBadges();
    const menuItems = this.#getMenuItems();
    const { info, tooltip: infoTooltip } = this.#getInfo();
    return html`
      ${this.#renderVerifyBankAccountDialog()}
      <div>
        <katapult-icon style="flex-shrink: 0;" .icon=${icon} size="20"></katapult-icon>
        <div style="overflow: hidden; text-overflow: ellipsis;">${label}</div>
        ${badges.map(({ variant, label, tooltip }) => {
          const badge = html`<sl-badge .variant=${variant} style="margin-left: var(--sl-spacing-2x-small);">${label}</sl-badge>`;
          return tooltip != null ? html`<sl-tooltip content=${tooltip}>${badge}</sl-tooltip>` : badge;
        })}
      </div>
      <div>
        ${info != null ? (infoTooltip != null ? html`<sl-tooltip content=${infoTooltip}>${info}</sl-tooltip>` : info) : nothing}

        <sl-dropdown style="position: relative;" hoist>
          <katapult-button slot="trigger" style="flex-shrink: 0;" iconOnly noBorder icon="more_vert" size="20"></katapult-button>
          <sl-menu @sl-select=${this.#optionsMenuSelectHandler.bind(this)}>
            ${menuItems.map(({ value, label, icon, tooltip, disabled = false }) => {
              const menuItemContent = html`
                ${icon != null ? html`<katapult-icon .icon=${icon} size="20"></katapult-icon>` : nothing}
                <div>${label}</div>
              `;
              return html` <sl-menu-item value=${value} .disabled=${disabled}>
                ${tooltip != null ? html`<sl-tooltip content=${tooltip} hoist>${menuItemContent}</sl-tooltip>` : menuItemContent}
              </sl-menu-item>`;
            })}
          </sl-menu>
        </sl-dropdown>
      </div>
    `;
  }

  /**
   * @returns {string}
   */
  #getIcon() {
    const { type } = this.paymentMethod ?? {};
    return PaymentMethod.GetIcon(type);
  }

  /**
   * @returns {string | null}
   */
  #getLabel() {
    const { paymentMethod } = this;
    if (!paymentMethod) return null;

    const { type } = paymentMethod;
    switch (type) {
      case 'card': {
        const { card } = paymentMethod;
        if (card == null) return 'UNKNOWN CARD';
        return `${capitalCase(card.brand)} •••• ${card.last4}`;
      }
      case 'us_bank_account': {
        const { us_bank_account: account } = paymentMethod;
        if (account == null) return 'UNKNOWN BANK';
        return `${account.bank_name} •••• ${account?.last4}`;
      }
      default:
        return 'UNKNOWN TYPE';
    }
  }

  /**
   * @returns {{
   *  info: string | undefined,
   *  tooltip: string | undefined
   * }}
   */
  #getInfo() {
    const { paymentMethod } = this;
    if (!paymentMethod) return { info: undefined, tooltip: undefined };

    const result = {};
    const { type } = paymentMethod ?? {};
    switch (type) {
      case 'card': {
        const { card } = paymentMethod;
        if (card == null) break;
        const expirationMonthName = LuxonInfo.months()[card.exp_month - 1];
        result.info = `${card.exp_month}/${card.exp_year.toString().slice(-2)}`;
        result.tooltip = `Expires ${expirationMonthName} ${card.exp_year}`;
        break;
      }
    }
    return result;
  }

  /**
   * @typedef {object} BadgeConfig
   * @property {SlBadge['variant']} variant
   * @property {string} label
   * @property {string} [tooltip]
   */
  /**
   * @returns {BadgeConfig[]}
   */
  #getBadges() {
    const { paymentMethod, usedByKatapultProSubscription, unverified } = this;
    const { type } = paymentMethod ?? {};
    /** @type {BadgeConfig[]} */
    const badges = [];
    if (type === 'us_bank_account') {
      badges.push({
        variant: /** @type {SlBadge['variant']} */ (unverified ? 'neutral' : 'success'),
        label: unverified ? 'Unverified' : 'Verified'
      });
    }
    if (usedByKatapultProSubscription) {
      badges.push({
        label: 'Default',
        tooltip: 'This payment method is used by your Katapult Pro subscription',
        variant: /** @type {SlBadge['variant']} */ ('primary')
      });
    }
    return badges;
  }
  /**
   * @typedef {object} MenuItemConfig
   * @property {string} value
   * @property {string} label
   * @property {string} [icon]
   * @property {string} [tooltip]
   * @property {boolean} [disabled]
   */
  /**
   * @returns {MenuItemConfig[]}
   */
  #getMenuItems() {
    const { paymentMethod, usedByKatapultProSubscription, unverified } = this;
    const { type } = paymentMethod ?? {};
    /** @type {MenuItemConfig[]} */
    const menuItems = [];
    if (type == 'us_bank_account' && unverified) {
      menuItems.push({ value: 'verify', label: 'Verify Bank Account', icon: 'check_circle' });
    }
    if (!usedByKatapultProSubscription) {
      menuItems.push({
        value: 'make_default',
        label: 'Use for Katapult Pro Subscription',
        icon: 'star',
        disabled: unverified,
        tooltip: unverified ? 'You must verify this payment method before it can be used by your Katapult Pro Subscription' : undefined
      });
    }
    menuItems.push({
      value: 'remove',
      label: 'Delete Payment Method',
      icon: 'delete',
      disabled: usedByKatapultProSubscription,
      tooltip: usedByKatapultProSubscription
        ? 'This payment method is used by your Katapult Pro subscription.  If you would like to delete it, first choose a different payment method to be used by your Katapult Pro Subscription.'
        : undefined
    });
    return menuItems;
  }

  #optionsMenuSelectHandler(e) {
    const { item } = e.detail;
    const { value } = item;
    switch (value) {
      case 'verify':
        this.#promptVerifyBankAccount();
        break;
      case 'make_default':
        this.#confirmMakeDefault();
        break;
      case 'remove':
        this.#confirmRemove();
        break;
    }
  }

  async #promptVerifyBankAccount() {
    if (!this._renderVerifyBankAccountDialog) {
      this._renderVerifyBankAccountDialog = true;
      this.requestUpdate();
      await this.updateComplete;
    }
    const dialog = this._verifyBankAccountDialogRef.value;
    if (!dialog) throw new Error('Dialog not found');
    dialog.open();
  }

  async #confirmVerifyBankAccount(e) {
    const button = /** @type {KatapultButton} */ (e?.currentTarget);
    const dialog = this._verifyBankAccountDialogRef.value;
    await this.#verifyBankAccount()
      .then(() => {
        button?.showSuccess();
        setTimeout(() => dialog?.close(), 2000);
      })
      .catch((err) => {
        button?.showError();
        if (err.message !== 'Form is invalid') throw err;
      });
  }

  async #verifyBankAccount() {
    const { companyId, paymentMethod } = this;
    if (!companyId) throw new Error('Company id is missing');
    if (!paymentMethod) throw new Error('Payment method is missing');

    const form = this._verifyBankAccountFormRef.value;
    if (!form) throw new Error('Form not found');

    const formIsValid = form.reportValidity();
    if (!formIsValid) throw new Error('Form is invalid');

    const formData = serialize(form);

    const microDeposit1 = Number(formData.micro_deposit_1);
    const microDeposit2 = Number(formData.micro_deposit_2);
    if (isNaN(microDeposit1)) throw new Error('Micro deposit 1 is not a number');
    if (isNaN(microDeposit2)) throw new Error('Micro deposit 2 is not a number');

    await VerifyCompanyBankAccount(companyId, paymentMethod.id, [microDeposit1, microDeposit2]);
    this.dispatchEvent(new CustomEvent('verified'));
  }

  async #confirmMakeDefault() {
    const { companyId, paymentMethod } = this;
    if (!paymentMethod) throw new Error('Payment method is missing');
    if (!companyId) throw new Error('Company id is missing');

    const confirmed = await KatapultDialog.confirm(
      `Are you sure that you want to use ${this.#getLabel()} for your Katapult Pro Subscription?`,
      'Use for Katapult Pro Subscription?'
    ).confirmed;
    if (!confirmed) return;
    await SetPaymentMethodUsedForCompanyKatapultProSubscription(companyId, paymentMethod.id);
    this.dispatchEvent(new CustomEvent('used-for-katapult-pro-subscription'));
  }

  async #confirmRemove() {
    const { companyId, paymentMethod } = this;
    if (!paymentMethod) throw new Error('Payment method is missing');
    if (!companyId) throw new Error('Company id is missing');

    const confirmed = await KatapultDialog.confirm({
      dialog: {
        /** @ts-ignore */
        title: 'Delete Payment Method',
        color: 'var(--sl-color-danger-500)'
      },
      text: `Are you sure that you want to delete ${this.#getLabel()}?`,
      confirmButtonText: {
        label: 'Delete',
        icon: 'delete'
      }
    }).confirmed;
    if (!confirmed) return;
    await RemoveCompanyPaymentMethod(companyId, paymentMethod.id);
    this.dispatchEvent(new CustomEvent('removed'));
  }
}
customElements.define('payment-method', PaymentMethod);
